import { customerSessionKey, MAIL_ID_REGEX } from "utils/constants";
import { getCustomerAccessToken, isValidToken } from "utils/oauth";
import getInfluenceDigest from "services/influence-customer-digest";
import { createAsyncThunk } from "@reduxjs/toolkit";

const updateCustomerSession = (customer) => {
	const customerState = sessionStorage.getItem(customerSessionKey);
	const existing = customerState ? JSON.parse(customerState) : {};
	const merged = { ...existing, ...customer };
	sessionStorage.setItem(customerSessionKey, JSON.stringify(merged));
};

const buildCustomerAddressInput = (address = {}, phone = "") => {
	const input = {
		address1: address.address1 || "",
		address2: address.address2 || "",
		city: address.city || "",
		company: address.company || "",
		firstName: address.firstName || "",
		lastName: address.lastName || "",
		zip: address.zip || "",
	};

	const countryCode = address.countryCode;
	const provinceCode = address.provinceCode;

	if (countryCode)
		input.territoryCode = countryCode;

	if (provinceCode)
		input.zoneCode = provinceCode;

	if (phone)
		input.phoneNumber = phone;

	return input;
};

const mapCustomerAddress = (address, phone) => ({
	...address,
	phoneNumber: address?.phoneNumber || phone,
});

export const influenceDigest = createAsyncThunk(
	"account/influenceDigest", async (user) => {
		const { emailAddress: { emailAddress }, id } = user;
		const digest = await getInfluenceDigest(emailAddress, id);
		return digest;
	}
);

export const getCustomerProfile = createAsyncThunk(
	"account/getCustomerProfile",
	async (forceRefresh = false, { dispatch, getState }) => {
		const { shopifyClient } = getState().global;
		const { digest } = getState().account.profile;
		const sessionCustomer = window.sessionStorage.getItem(customerSessionKey);
		const user = sessionCustomer && !forceRefresh ? JSON.parse(sessionCustomer) : await shopifyClient.customer.getProfile();
		const token = getCustomerAccessToken();

		if (!user || (sessionCustomer && !isValidToken(token))) {
			throw new Error("User is not authenticated");
		}
		if (user && !digest) dispatch(influenceDigest(user));

		updateCustomerSession(user);

		return user;
	}
);

export const fetchCustomerAddresses = createAsyncThunk(
	"account/fetchCustomerAddresses",
	async (cursor = null, { getState }) => {
		const { shopifyClient } = getState().global;
		const phone = getState().account.profile.phoneNumber?.phoneNumber || "";
		const customer = await shopifyClient.customer.getAddresses(cursor);
		if (!customer) throw new Error("Failed to fetch customer addresses");

		const mappedAddresses = Object.fromEntries(
			customer?.addresses?.nodes.map(addr => [
				addr.id.match(MAIL_ID_REGEX)[1],
				mapCustomerAddress(addr, phone)
			])
		);

		return {
			addresses: mappedAddresses,
			pageInfo: customer?.addresses?.pageInfo,
			defaultAddress: customer?.defaultAddress ? mapCustomerAddress(customer.defaultAddress, phone) : null,
		};
	}
);

export const updateCustomerDefaultAddress = createAsyncThunk(
	"account/updateCustomerDefaultAddress",
	async (addressId, { getState, rejectWithValue }) => {
		const { shopifyClient } = getState().global;
		const phone = getState().account.profile.phoneNumber?.phoneNumber || "";
		const cleanId = addressId?.match(MAIL_ID_REGEX)?.[1] ?? null;
		const response = await shopifyClient.customer.changeDefaultAddress(addressId);

		if (!response || response?.customerUserErrors?.length) {
			return rejectWithValue({ success: false });
		}

		return {
			success: true,
			address: mapCustomerAddress(response.customerAddress, phone),
			id: cleanId,
		};
	}
);

export const updateCustomerAddress = createAsyncThunk(
	"account/updateCustomerAddress",
	async ({ address, addressId, cleanId, defaultAddress }, { getState, rejectWithValue }) => {
		const { shopifyClient } = getState().global;
		const phone = getState().account.profile.phoneNumber?.phoneNumber || "";
		const addressPayload = buildCustomerAddressInput(address, phone);
		const response = await shopifyClient.customer.updateAddress(addressId, addressPayload, defaultAddress);

		if (!response || response?.userErrors?.length) {
			return rejectWithValue({ success: false, errors: response?.userErrors });
		}

		return {
			success: true,
			address: mapCustomerAddress(response.customerAddress, phone),
			id: cleanId
		};
	}
);

export const createCustomerAddress = createAsyncThunk(
	"account/createCustomerAddress",
	async ({ address, defaultAddress }, { getState, rejectWithValue }) => {
		const { shopifyClient } = getState().global;
		const phone = getState().account.profile.phoneNumber?.phoneNumber || "";
		const addressPayload = buildCustomerAddressInput(address, phone);
		const response = await shopifyClient.customer.createAddress(addressPayload, defaultAddress);

		if (!response || response?.userErrors?.length) {
			return rejectWithValue({ success: false, errors: response?.userErrors });
		}

		const result = response?.customerAddress;
		return {
			success: true,
			address: mapCustomerAddress(result, phone),
			id: result?.id?.match(MAIL_ID_REGEX)[1]
		};
	}
);

export const deleteCustomerAddress = createAsyncThunk(
	"account/deleteCustomerAddress",
	async ({ id, cleanId }, { getState, rejectWithValue }) => {
		const { shopifyClient } = getState().global;
		const response = await shopifyClient.customer.deleteAddress(id);

		if (!response || response?.userErrors?.length) {
			return rejectWithValue({ success: false });
		}

		return { success: true, id: cleanId };
	}
);

export const updateCustomerInfo = createAsyncThunk(
	"account/updateCustomerInfo",
	async (customerData, { getState, rejectWithValue }) => {
		const { shopifyClient } = getState().global;

		const response = await shopifyClient.customer.update(customerData);

		const errors = response?.customerUpdate?.customerUserErrors;

		if (!response || (errors && errors.length > 0)) {
			return rejectWithValue({ success: false });
		}

		const customer = response?.customer;

		if (!customer) {
			return rejectWithValue({ success: false });
		}

		updateCustomerSession(customer);

		return { success: true, customer };
	}
);

export const fetchCustomerOrders = createAsyncThunk(
	"account/fetchCustomerOrders",
	async (pageInfo, { getState }) => {
		const customerId = getState().account.profile.id;
		const { shopifyClient } = getState().global;

		const [adminResponse, storefrontResponse] = await Promise.all([
			shopifyClient.customer.getOrdersFromAdmin(customerId, pageInfo?.admin?.endCursor),
			shopifyClient.customer.getOrders(pageInfo?.storefront?.endCursor),
		]).catch((err) => {
			console.error("Error fetching customer orders:", err);
			return [];
		});

		const payload = {
			orders: adminResponse?.orders?.map((order) => {
				const storefrontOrder = storefrontResponse?.orders?.nodes?.find(o => o.name === order.name);
				return {
					...order,
					statusPageUrl: storefrontOrder?.statusPageUrl,
				};
			}),
			numberOfOrders: adminResponse?.numberOfOrders,
			pageInfo: {
				admin: adminResponse?.pageInfo,
				storefront: storefrontResponse?.orders?.pageInfo,
			},
		};

		if (!adminResponse?.orders?.length || adminResponse?.customerUserErrors?.length) {
			throw new Error("Failed to fetch customer orders");
		}

		return payload;
	}
);
